import {getBuild} from '../selectors'
import type {BuildId} from '../types'

import {useAppSelector} from './react-redux'
import {useIsMine} from './useIsMine'
import {useUserDisplayName} from './useUserDisplayName'

function useBuildsOwnerUserName(buildId: BuildId): string | null | undefined {
  const buildsOwner = useAppSelector(state => getBuild(state, buildId)?.user)
  return useUserDisplayName(buildsOwner)
}

function useBuildPrefix(buildId: BuildId): string {
  const personal = useAppSelector(state => getBuild(state, buildId)?.personal)
  const isMine = useIsMine(buildId)
  const ownerUserName = useBuildsOwnerUserName(buildId) || 'unknown user'

  if (personal !== true) {
    return 'Build'
  }

  if (isMine) {
    return 'Your personal build'
  }

  return `Personal build by ${ownerUserName}`
}

function useBuildStatusText(buildId: BuildId): string {
  const status = useAppSelector(state => getBuild(state, buildId)?.status)
  const buildState = useAppSelector(state => getBuild(state, buildId)?.state)
  const canceledInfo = useAppSelector(state => getBuild(state, buildId)?.canceledInfo)
  const failedToStart = useAppSelector(state => getBuild(state, buildId)?.failedToStart)
  const canceledComment = canceledInfo?.text
  const canceledByUserName = useUserDisplayName(canceledInfo?.user)
  const isGreen = status == null || status === 'SUCCESS'

  if (canceledInfo != null) {
    return [
      'canceled',
      canceledByUserName != null && `by ${canceledByUserName}`,
      canceledComment != null && `with comment: ${canceledComment}`,
    ]
      .filter(Boolean)
      .join(' ')
  }

  if (buildState === 'queued') {
    return 'waiting in the queue'
  }

  if (buildState === 'running') {
    return `is ${isGreen ? 'running' : 'failing'}`
  }

  if (isGreen) {
    return 'was successful'
  }

  if (failedToStart === true) {
    return 'failed to start'
  }

  return 'failed'
}

export function useBuildStatusTooltip(buildId: BuildId): string {
  const buildPrefix = useBuildPrefix(buildId)
  const buildStatusText = useBuildStatusText(buildId)
  return `${buildPrefix} ${buildStatusText}`
}
